import React, { useEffect, useState } from 'react';
import ISelectOption from '@/models/generic/iselect-option';
import IRetreat from '@/models/retreats/iretreat';
import * as retreatService from '@/services/retreats-service';
import * as configService from '@/services/config-service';
import DropdownComponent from '@/components/forms/dropdown-component';
import NumberComponent from '@/components/forms/number-component';
import DatePickerComponent from "@/components/forms/datepicker-component";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import ConfirmationModal from "@/components/modals/confirmation";
import Spinner from "@/components/spinner";
import { useNavigate } from "react-router";
import YesAndNoDropdownComponent from "@/components/forms/yes-no-dropdown-component";
import { RetreatHasChanged, RetreatHasRequiredFormFields } from "./validation";

interface IProps {
    id?: number;
    closeModal: (requiresUpdate: boolean) => void;
}

const EditRetreat: React.FC<IProps> = ({ id, closeModal }) => {
    const [ isLoading, setIsLoading ] = useState<boolean>(true);
    const [ retreat, setRetreat] = useState<IRetreat>({} as IRetreat);
    const [ instructors, setInstructors ] = useState<ISelectOption[]>([]);
    const [ retreatStatuses, setRetreatStatuses ] = useState<ISelectOption[]>([]);
    const [ venues, setVenues ] = useState<ISelectOption[]>([]);
    const [ seasons, setSeasons ] = useState<ISelectOption[]>([]);
    const [ retreatCostTypes, setRetreatCostTypes ] = useState<ISelectOption[]>([]);

    const [ originalRetreat, setOriginalRetreat] = useState<IRetreat>({} as IRetreat);
    const [ showConfirmationCloseModal, setShowConfirmationCloseModal ] = useState<boolean>(false);
    const [ showConfirmationDeletemodal, setShowConfirmationDeletemodal ] = useState<boolean>(false);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [ retreat, instructors, retreatStatuses, venues, seasons, retreatCostTypes ] = await Promise.all([
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    retreatService.getRetreat(id),
                    configService.GetInstructors(),
                    configService.GetRetreatStatuses(),
                    configService.GetVenues(),
                    configService.GetSeasons(),
                    configService.GetRetreatCostTypes()
                ]);

                if (retreat.occupancy === 0) {
                    retreat.occupancy = undefined;
                }

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                setRetreat(retreat); // Is this an issue? It is throwing an error in vs code but nowhere else I don't believe
                setInstructors(instructors);
                setRetreatStatuses(retreatStatuses);
                setVenues(venues);
                setSeasons(seasons);
                setRetreatCostTypes(retreatCostTypes);

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                setOriginalRetreat(retreat);

            } catch {
                setIsLoading(false);
                closeModal(false);
                toast.error("The retreat couldn't be loaded for an unknown reason.");
            }

            setIsLoading(false);
        }

        fetchData();
    }, [])

    const submitChanges = async () => {
        if (retreat !== undefined && RetreatHasChanged(originalRetreat, retreat)) {
            const update = async () => {

                if (retreat.isGroupBooking == false) {
                    retreat.manageRooming = true;
                    retreat.occupancy = 0;
                }
                
                await retreatService.updateRetreat(retreat);
            }

            await toast.promise(
                update(),
                {
                    pending: 'Saving retreat',
                    success: 'Retreat saved',
                    error: 'Something went wrong whilst trying to save the retreat'
                }
            )

            closeModal(true);
        }
        else {
            closeModal(false);
        }
    }

    function requestToCloseModal() {
        if (RetreatHasChanged(originalRetreat, retreat)) {
            setShowConfirmationCloseModal(true);
        }
        else {
            closeModal(false);
        }
    }

    function receivedConfirmationCloseRequest(wantsToClose: boolean) {
        setShowConfirmationCloseModal(false);
        
        if (wantsToClose) {
            closeModal(false);
        }
    }

    async function requestToDelete() {
        setShowConfirmationDeletemodal(true);
    }

    async function receivedConfirmationDeleteRequest(wantsToDelete: boolean) {
        setShowConfirmationDeletemodal(false);
        
        if (wantsToDelete) {
            const remove = async () => {
                await retreatService.deleteRetreat(retreat?.id);
            }
    
            await toast.promise(
                remove(),
                {
                    pending: "Removing retreat and all it's guests",
                    success: "Removed the retreat and it's guests",
                    error: "Something went wrong whilst trying to remove the retreat and it's guests"
                }
            )
            
            navigate('/');
        }

    }

    return (
        <div className="w-11/12 lg:w-3/4 mx-auto">
            <div className='bg-br-blue-300'>
                
                <Spinner isLoading={isLoading}>
                    <div>
                        <h1 className="text-4xl md:text-6xl font-thin text-center pb-1 pt-3">Edit Retreat</h1>
                        <hr className="mx-auto mt-3" />

                        <div className='mx-2 grid grid-cols-2 pb-4 pt-2'>
                            <DatePickerComponent
                                value={retreat?.startDate}
                                purpose="Start Date"
                                newValue={(newValue) => setRetreat({...retreat, startDate: newValue} as IRetreat)} 
                            />

                            <DatePickerComponent
                                value={retreat?.endDate}
                                purpose="End Date"
                                newValue={(newValue) => setRetreat({...retreat, endDate: newValue} as IRetreat)} 
                            />

                            <DropdownComponent
                                value={retreat?.seasonId}
                                values={seasons}
                                purpose="Season"
                                newValue={(newValue) => setRetreat({...retreat, seasonId: newValue} as IRetreat)} 
                            />

                            <DropdownComponent
                                value={retreat?.instructorId}
                                values={instructors}
                                purpose="Instructor"
                                newValue={(newValue) => setRetreat({...retreat, instructorId: newValue} as IRetreat)} 
                            />

                            <DropdownComponent
                                value={retreat?.venueId}
                                values={venues}
                                purpose="Venue"
                                newValue={(newValue) => setRetreat({...retreat, venueId: newValue} as IRetreat)} 
                            />

                            <DropdownComponent
                                value={retreat?.statusId}
                                values={retreatStatuses}
                                purpose="Status"
                                newValue={(newValue) => setRetreat({...retreat, statusId: newValue} as IRetreat)} 
                            />

                            <DropdownComponent
                                value={retreat?.retreatCostTypeId}
                                values={retreatCostTypes}
                                purpose="Retreat Costing"
                                newValue={(newValue) => setRetreat({...retreat, retreatCostTypeId: newValue} as IRetreat)} 
                            />

                            <YesAndNoDropdownComponent
                                value={retreat.isGroupBooking}
                                purpose="Is this a group booking?"
                                newValue={(newValue) => setRetreat({...retreat, isGroupBooking: newValue} as IRetreat)} 
                            />

                            {retreat?.isGroupBooking && (
                                <>
                                    <YesAndNoDropdownComponent
                                        value={retreat.manageRooming}
                                        purpose="Would you like to manage the rooming?"
                                        newValue={(newValue) => setRetreat({...retreat, manageRooming: newValue} as IRetreat)} 
                                    />
                                    
                                    <NumberComponent
                                        value={retreat?.occupancy}
                                        purpose="Number of guests"
                                        newValue={(newValue) => setRetreat({...retreat, occupancy: newValue} as IRetreat)} 
                                    />

                                    <NumberComponent
                                        value={retreat?.retailPrice}
                                        purpose="Full price"
                                        newValue={(newValue) => setRetreat({...retreat, retailPrice: newValue} as IRetreat)} 
                                    />
                                </>
                            )}

                        </div>

                        <div className="flex justify-end mx-5">
                            <button className="px-3 py-1 mx-1 mb-2 bg-green-500 hover:brightness-110 disabled:opacity-50" onClick={submitChanges} disabled={!RetreatHasRequiredFormFields(retreat) || !RetreatHasChanged(originalRetreat, retreat)}>Save</button>
                            <button className="px-3 py-1 mx-1 mb-2 bg-red-600 hover:brightness-110" onClick={requestToCloseModal}>Cancel</button>
                            <button className="px-3 py-1 mx-1 mb-2 bg-red-600 hover:brightness-110" onClick={requestToDelete}><i className="fas fa-trash-alt text-white" /></button>
                        </div>

                        {showConfirmationCloseModal && <ConfirmationModal confirmationText="Are you sure you want to cancel updating this retreat?" closeModal={(response) => receivedConfirmationCloseRequest(response)} />}
                        {showConfirmationDeletemodal && <ConfirmationModal confirmationText="Are you sure you want to delete this retreat and all it's guest?" closeModal={(response) => receivedConfirmationDeleteRequest(response)} />}
                    </div>
                </Spinner>
            </div>
        </div>
    );
}

export default EditRetreat;